"use client";

// src/components/organisms/legal/cookie-consent-provider.tsx
// Mounted once in the root layout (src/app/layout.tsx) so the banner shows on
// every surface. Shows the cookie banner when no valid consent exists;
// listens for the global `OPEN_PREFERENCES_EVENT` to re-open the preferences
// panel from the footer.
//
// Analytics gates (GA, HubSpot) read `getConsent()` directly and subscribe to
// `CONSENT_CHANGED_EVENT`, dispatched on every save.

import { useCallback, useEffect, useState } from "react";
import { CookieBanner } from "@/components/molecules/legal/cookie-banner";
import {
  CONSENT_CHANGED_EVENT,
  type ConsentState,
  getConsent,
  hasValidConsent,
  OPEN_PREFERENCES_EVENT,
} from "@/lib/consent";

type Visibility = "hidden" | "auto" | "preferences";

export function CookieConsentProvider() {
  const [visibility, setVisibility] = useState<Visibility>("hidden");
  const [initialConsent, setInitialConsent] = useState<ConsentState | null>(null);

  // Client-only mount guard: only decide visibility after hydration so SSR
  // renders nothing (avoids hydration mismatches and FOUC of a missing
  // banner). This can't be "adjusted during render" instead (the usual fix
  // for react-hooks/set-state-in-effect) because render also runs on the
  // server, which is exactly what this guard exists to avoid — the one
  // extra client-only render this causes is the intended behavior, not the
  // "cascading renders" the rule is warning about.
  useEffect(() => {
    const current = getConsent();
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setInitialConsent(current);
    if (!hasValidConsent()) {
      setVisibility("auto");
    }
  }, []);

  // Footer "Cookie preferences" link → re-open with panel expanded.
  useEffect(() => {
    function onOpen() {
      setInitialConsent(getConsent());
      setVisibility("preferences");
    }
    window.addEventListener(OPEN_PREFERENCES_EVENT, onOpen);
    return () => window.removeEventListener(OPEN_PREFERENCES_EVENT, onOpen);
  }, []);

  const handleDismiss = useCallback((state: ConsentState) => {
    setVisibility("hidden");
    setInitialConsent(state);
    window.dispatchEvent(new CustomEvent(CONSENT_CHANGED_EVENT, { detail: state }));
  }, []);

  if (visibility === "hidden") return null;

  return (
    <CookieBanner
      key={visibility}
      initialConsent={initialConsent}
      openPreferencesOnMount={visibility === "preferences"}
      onDismiss={handleDismiss}
    />
  );
}
